import { useEffect, useState } from "react"

import { Link, useParams } from "react-router-dom"
import { BsActivity, BsYoutube } from "react-icons/bs"
import { FaArrowLeft, FaArrowRight, FaExternalLinkSquareAlt, FaStar } from "react-icons/fa"
import {BiCheck, BiDownload, BiHeart, BiLeftArrow, BiRightArrow, BiUser } from "react-icons/bi"
import { CiShare2 } from "react-icons/ci"
import { FiExternalLink } from "react-icons/fi"
import { RiSeparator } from "react-icons/ri"
import RelatedProjects from "../projects/RelatedProjects"
import CTASection from "../projects/CTASection"
import projectList from "../assets/projectList"

export default function ProjectDetailPage() {
  const { portfolioId } = useParams(); // Ensure param name matches the route
  const portfolio = projectList.find((p) => String(p.id) === portfolioId);
  const images = portfolio?.project_images ?? [];
  const [isLiked, setIsLiked] = useState(false)
  const [activeTab, setActiveTab] = useState("description");
  const [mainImage, setMainImage] = useState(images[0] || "");
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top when pathname changes
      },);

    useEffect(() => {
      if (images.length > 0) {
        setMainImage(images[0]);
        setCurrentIndex(0);
      }
    }, [images]);
  
    if (!portfolio) {
      return <h2 className="text-center text-red-500 text-xl">Project Not Found</h2>;
    }

    const handlePrev = () => {
      const newIndex = (currentIndex - 1 + images.length) % images.length;
      setCurrentIndex(newIndex);
      setMainImage(images[newIndex]);
    };

  const handleNext = () => {
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    setMainImage(images[newIndex]);
  };
  
  return (
    <div>
    <div className="container max-w-7xl mx-auto px-4 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
         {/* Product Image Section */}
         <div className="space-y-8">
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-lg">
            <img
              src={mainImage}
              alt={portfolio.title}
              className="object-cover w-full h-full"
            />
            
            {images.length > 1 && (
              <>
                <button 
                  onClick={handlePrev} 
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full">
                  <FaArrowLeft className="h-6 w-6" />
                </button>
                <button 
                  onClick={handleNext} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full">
                  <FaArrowRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
          <div className="grid grid-cols-4 gap-4">
            {images.map((image, i) => (
              <div 
                key={i} 
                onClick={() => {
                  setMainImage(image);
                  setCurrentIndex(i);
                }} 
                className={`aspect-square rounded-xl overflow-hidden shadow-md cursor-pointer ${
                  currentIndex === i ? "border-2 border-cyan-500" : ""
                }`}
                
              >
                <img
                  src={image}
                  alt={`Thumbnail ${i + 1}`}
                  className="object-cover w-full h-full"
                />
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Product Details Section */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-4">
              <button className="px-3 py-1 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border-cyan-200 font-medium">
              {portfolio.category}
              </button>
              <button variant="ghost" size="icon" onClick={() => setIsLiked(!isLiked)} aria-label="Add to favorites">
                <BiHeart className={`h-5 w-5 ${isLiked ? "fill-red-500 text-red-500" : ""}`} />
              </button>
            </div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">{portfolio.title}</h1>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar key={star} className="h-5 w-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm text-muted-foreground font-medium">4.9 • 12.5k reviews</span>
            </div>

            <div className="flex gap-4 mb-6">
            <button className="flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white py-3 px-4 rounded">
  <BiDownload className="h-5 w-5" />
  Download App
</button>
              <button variant="outline" size="icon" className="h-12 w-12">
                <CiShare2 className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* YouTube Link - Professional Version */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-0 shadow-sm">
            <div className="flex items-center gap-4 p-4">
              <div className="bg-cyan-500 rounded-full p-3 flex-shrink-0">
                <BsYoutube className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-cyan-800">Video Demonstrations</h3>
                <p className="text-sm text-cyan-700 mb-1">
                  Access our complete library of professional workout tutorials
                </p>
                <Link
                  to={portfolio.videoUrl}
                  className="text-sm text-cyan-600 hover:text-cyan-800 font-medium flex items-center"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on YouTube
                  <FaExternalLinkSquareAlt className="h-3 w-3 ml-1" />
                </Link>
              </div>
            </div>
          </div>

          {/* Cyan Profile Link - Alternative Version */}
          <div className="bg-white border border-cyan-200 shadow-sm overflow-hidden rounded-xl">
            <div className="p-0">
              <div className="flex flex-col sm:flex-row">
                <div className="bg-cyan-500 p-5 flex items-center justify-center sm:w-1/3">
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 mx-auto mb-2 w-fit">
                      <BiUser className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="text-white font-medium text-sm">Trainer Profile</h4>
                  </div>
                </div>
                <div className="p-5 sm:w-2/3">
                  <h3 className="font-semibold text-cyan-800 mb-1">Connect with Certified Trainers</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Get personalized advice and custom workout plans from our expert trainers
                  </p>
                  <Link
                    to={portfolio.trainerLink}
                    className="text-cyan-600 hover:text-cyan-800 font-medium text-sm flex items-center w-fit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Trainer Profile
                    <FiExternalLink className="h-3.5 w-3.5 ml-1.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      
         {/* Tabs Navigation */}
         <div className="w-full my-2">
            <div className="grid grid-cols-3 h-12 border-b">
              {["description"].map((tab) => (
                <button
                  key={tab}
                  className={`text-sm py-2 border-b-2 transition-all ${
                    activeTab === tab ? "border-cyan-500 text-cyan-600 font-bold" : "text-gray-500"
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tabs Content */}
<div className="pt-6 space-y-6">
  {activeTab === "description" && (
    <div className="pt-6 space-y-6">
      <div className="text-muted-foreground leading-relaxed">
        {portfolio.description.split("\n").map((paragraph, i) => (
          <p key={i} className="mb-4">{paragraph}</p>
        ))}
      </div>
    </div>
  )}

</div>

          </div>

          <RiSeparator className="my-2" />

          <div className="pt-2">
  <h3 className="font-semibold mb-4 text-lg">App Information</h3>
  <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
    <div>
      <dt className="text-muted-foreground mb-1">Developed By</dt>
      <dd className="font-medium">{portfolio.developer || "Unknown"}</dd>
    </div>
    <div>
      <dt className="text-muted-foreground mb-1">Version</dt>
      <dd className="font-medium">{portfolio.version || "N/A"}</dd>
    </div>
    <div>
      <dt className="text-muted-foreground mb-1">Size</dt>
      <dd className="font-medium">{portfolio.size || "N/A"}</dd>
    </div>
    <div>
      <dt className="text-muted-foreground mb-1">Compatibility</dt>
      <dd className="font-medium">{portfolio.compatibility || "N/A"}</dd>
    </div>
    <div>
      <dt className="text-muted-foreground mb-1">Languages</dt>
      <dd className="font-medium">{portfolio.languages?.join(", ") || "N/A"}</dd>
    </div>
    <div>
      <dt className="text-muted-foreground mb-1">Category</dt>
      <dd className="font-medium">{portfolio.category || "N/A"}</dd>
    </div>
  </dl>
</div>

    </div>
    <div>
          <RelatedProjects/>
          </div>
          <div>
            <CTASection/>
          </div>
    </div>
  )
}
